import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { MeSicAssignmentService } from './me-sic-assignment.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-me-sic-assignment',
    templateUrl: './me-sic-assignment.component.html',
    styleUrls: ['./me-sic-assignment.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MeSicAssignmentComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties
  modalTitle = "ME SIC Code Assignment";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  // Component data properties
  rowData: any = {};
  filterParams: any = {};
  gridData: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  SERVER_CONFIG: any = {};

  // Form data
  selectedTaxYear: string = '';
  selectedMEKey: string = '';
  selectedSicCodes: any[] = [];
  selectedSicCodesTemp: any[] = [];
  sicCodeData: any[] = [];
  sicCodeDataTemp: any[] = [];
  bslaCodeData: any[] = [];
  parentMeData: any[] = [];
  newParentMeList: any[] = [];

  // Operation type
  operationType: string = 'I'; // 'I' for Insert, 'U' for Update

  // UI state
  showParentMeDropdown: boolean = true;
  showOptionSicCodeSelect: boolean = false;
  isLocked: boolean = false;

  // User settings
  logged_in_user: string = '';

  // Searchable multiselect dropdown state
  sicCodeDropdownOpen: boolean = false;
  sicCodeSearchText: string = '';
  sicCodeFilteredData: any[] = [];

  // Initialization flags
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private meSicAssignmentService: MeSicAssignmentService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.globalService = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp);
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.filterParams = fp;
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : (row || {});
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = g != null && typeof g === 'object' ? g : {};
  }

  // Gate function to prevent duplicate initialization
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      // Get logged in user
      if (this.USER_SETTINGS?.user?.sso_id) {
        this.logged_in_user = this.USER_SETTINGS.user.sso_id;
      }

      // Check lock status
      this.checkLockStatus();

      // Load initial data (determines edit/add mode)
      this.loadInitialData();

      // Load Parent ME dropdown (will set selectedMEKey in edit mode after data loads)
      this.getParentMeDropdown();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Check lock status to prevent editing when tax return is filed
   * Action code: n10cpv
   */
  checkLockStatus(): void {
    const scenario = this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '';
    const jcd_key = this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250';

    const params: any = {
      action_code: 'n10cpv',
      p_scenario: scenario,
      p_jcd_key: jcd_key
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      null,
      null
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject) {
          this.isLocked = data.jsonObject[0]?.LOCK_STATUS === 'Y';
          if (this.isLocked) {
            const taxYear = this.filterParams?.tax_year || this.globalService?.tax_year || '';
            this.gtwSnackbarService.openSnackBar(
              `Tax Return has already been filed for tax year ${taxYear}.`,
              'gtw-snackbar--red'
            );
            this.cancel();
          }
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  /**
   * Get Parent ME (BSLA) Dropdown
   * Action code: vxg30f
   */
  getParentMeDropdown(): void {
    // 🔴 CRITICAL: Include ALL required parameters with proper fallback chains for action code vxg30f
    const params: any = {
      action_code: 'vxg30f',
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || this.globalService?.globalParams?.activity_key || this.rowData?.ACTIVITY_KEY || '1349',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || this.globalService?.globalParams?.is_issue_key_enabled || 'N',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || this.globalService?.globalParams?.is_locked || this.rowData?.IS_LOCKED || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || this.globalService?.globalParams?.issue_id || this.rowData?.ISSUE_ID || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.globalService?.globalParams?.issue_id_for_tx_save || this.rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.globalService?.globalParams?.issue_short_desc || this.rowData?.ISSUE_SHORT_DESC || '',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.globalService?.globalParams?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.globalService?.globalParams?.jcd_ta_key || this.rowData?.JCD_TA_KEY || '1250',
      parent_me_string: this.filterParams?.parent_me_string || this.globalService?.parent_me_string || this.globalService?.globalParams?.parent_me_string || this.rowData?.PARENT_ME_STRING || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.globalService?.globalParams?.scenario || this.rowData?.SCENARIO || '74',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || this.globalService?.globalParams?.scenarioCode || this.globalService?.globalParams?.scenario_code || 'RAF',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || this.rowData?.SCREEN_KEY || '376',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || this.globalService?.globalParams?.screen_type || this.rowData?.SCREEN_TYPE || 'grid',
      sso_id: this.logged_in_user || this.USER_SETTINGS?.user?.sso_id || this.filterParams?.sso_id || this.globalService?.sso_id || this.globalService?.globalParams?.sso_id || this.rowData?.SSO_ID || '',
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.globalService?.globalParams?.tax_year || this.rowData?.TAX_YEAR || '2024'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject',
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject) {
          this.bslaCodeData = data.jsonObject;
          
          // 🔴 CRITICAL: Following AngularJS pattern - Set selectedMEKey AFTER dropdown data is loaded
          // In edit mode, find the matching object from dropdown array (like AngularJS .find() pattern)
          if (this.gridData != null && this.gridData.rowData != undefined && this.operationType === 'U') {
            const selectedRow = this.gridData.rowData;
            const meKeyToMatch = String(selectedRow.ME_KEY || '');
            
            // Find matching object from bslaCodeData (following AngularJS pattern)
            const matchedBSLA = this.bslaCodeData.find((bsla: any) => 
              String(bsla.PARENT_ME_KEY) === meKeyToMatch
            );
            
            if (matchedBSLA) {
              this.selectedMEKey = String(matchedBSLA.PARENT_ME_KEY);
              // Now that selectedMEKey is set and dropdown data is loaded, trigger filterParentMeSelection
              this.filterParentMeSelection();
            } else {
              this.selectedMEKey = meKeyToMatch; // Fallback to original value if not found
              this.filterParentMeSelection();
            }
          }
          
          this.cdr.detectChanges();
        }
      },
      (error: any) => {
        console.error('Error loading Parent ME dropdown:', error);
      }
    );
  }

  /**
   * Load initial data based on edit/add mode
   */
  loadInitialData(): void {
    if (this.gridData != null && this.gridData.rowData != undefined) {
      // Edit mode
      const selectedRow = _.clone(this.gridData.rowData);
      this.operationType = 'U';
      this.selectedTaxYear = selectedRow.TAX_YEAR || '';
      // 🔴 CRITICAL: Don't set selectedMEKey here - it will be set in getParentMeDropdown() after dropdown data loads
      // Following AngularJS pattern: set selected value AFTER dropdown data is loaded (using .find() pattern)
      this.showParentMeDropdown = false;
      // Note: filterParentMeSelection() will be called after getParentMeDropdown() completes
      // We need to wait for dropdown data to load before setting selectedMEKey
    } else {
      // Add mode
      this.operationType = 'I';
      this.selectedTaxYear = this.filterParams?.tax_year || this.globalService?.tax_year || '';
      this.selectedMEKey = ''; // Clear for add mode
      this.showParentMeDropdown = true;
      this.showOptionSicCodeSelect = false;
    }
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  /**
   * Filter Parent ME Selection - Load SIC codes and saved data
   * Action codes: 3vvxs7 (filter), ejsaqd (load saved)
   */
  filterParentMeSelection(): void {
    if (!this.selectedMEKey || !this.selectedTaxYear) {
      return;
    }

    this.showOptionSicCodeSelect = false;
    this.sicCodeData = [];
    this.selectedSicCodes = [];
    this.selectedSicCodesTemp = [];
    this.newParentMeList = [];

    // 🔴 CRITICAL: Include ALL required parameters with proper fallback chains (matching action code 3vvxs7)
    const params: any = {
      activity_key: this.filterParams?.activity_key || this.globalService?.activity_key || this.rowData?.ACTIVITY_KEY || '1349',
      is_issue_key_enabled: this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      is_locked: this.filterParams?.is_locked || this.globalService?.is_locked || this.rowData?.IS_LOCKED || 'N',
      issue_id: this.filterParams?.issue_id || this.globalService?.issue_id || this.rowData?.ISSUE_ID || '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      issue_short_desc: this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.rowData?.ISSUE_SHORT_DESC || '',
      jcd_key: this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.rowData?.JCD_TA_KEY || '1250',
      parent_me_string: this.selectedMEKey || this.filterParams?.parent_me_string || this.globalService?.parent_me_string || '',
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '74',
      scenarioCode: this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      screen_key: this.filterParams?.screen_key || this.globalService?.screen_key || this.rowData?.SCREEN_KEY || '376',
      screen_type: this.filterParams?.screen_type || this.globalService?.screen_type || this.rowData?.SCREEN_TYPE || 'grid',
      tax_year: this.selectedTaxYear || this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '2024'
    };

    // Load filter data (SIC codes)
    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=3vvxs7',
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === '1' && data.jsonObject) {
          this.parentMeData = data.jsonObject;
          
          if (Array.isArray(this.parentMeData) && this.parentMeData.length > 0) {
            for (let i in this.parentMeData) {
              // Skip YES_NO dropdown
              if (this.parentMeData[i].OPTION_CODE_MAP != 'YES_NO') {
                this.newParentMeList.push(_.clone(this.parentMeData[i]));
              }
              if (this.parentMeData[i].CODE_GROUP === 'SIC_CODE') {
                this.parentMeData[i]['SIC_CODE_DESC'] = this.parentMeData[i].CODE_DISP_DESC;
                this.sicCodeData.push(this.parentMeData[i]);
              }
            }
          }
          // Initialize filtered data for searchable dropdown
          this.sicCodeFilteredData = [...this.sicCodeData];
        }

        // Load saved data
        this.loadSavedData(params);
      },
      (error: any) => {
        console.error('Error loading filter data:', error);
      }
    );
  }

  /**
   * Load saved data for selected ME and Tax Year
   * Action code: ejsaqd
   */
  loadSavedData(params: any): void {
    // 🔴 CRITICAL: Ensure ALL required parameters are included (matching action code ejsaqd)
    // Params already include all required fields from filterParentMeSelection, but ensure action_code is set
    const loadParams: any = {
      ...params,
      // Ensure all required parameters are present with proper fallbacks
      activity_key: params.activity_key || this.filterParams?.activity_key || this.globalService?.activity_key || this.rowData?.ACTIVITY_KEY || '1349',
      is_issue_key_enabled: params.is_issue_key_enabled || this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      is_locked: params.is_locked || this.filterParams?.is_locked || this.globalService?.is_locked || this.rowData?.IS_LOCKED || 'N',
      issue_id: params.issue_id || this.filterParams?.issue_id || this.globalService?.issue_id || this.rowData?.ISSUE_ID || '',
      issue_id_for_tx_save: params.issue_id_for_tx_save || this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || this.rowData?.ISSUE_ID_FOR_TX_SAVE || '',
      issue_short_desc: params.issue_short_desc || this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || this.rowData?.ISSUE_SHORT_DESC || '',
      jcd_key: params.jcd_key || this.filterParams?.jcd_key || this.globalService?.jcd_key || this.rowData?.JCD_KEY || '250',
      jcd_ta_key: params.jcd_ta_key || this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || this.rowData?.JCD_TA_KEY || '1250',
      parent_me_string: params.parent_me_string || this.selectedMEKey || this.filterParams?.parent_me_string || this.globalService?.parent_me_string || '',
      scenario: params.scenario || this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO || '74',
      scenarioCode: params.scenarioCode || this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      screen_key: params.screen_key || this.filterParams?.screen_key || this.globalService?.screen_key || this.rowData?.SCREEN_KEY || '376',
      screen_type: params.screen_type || this.filterParams?.screen_type || this.globalService?.screen_type || this.rowData?.SCREEN_TYPE || 'grid',
      tax_year: params.tax_year || this.selectedTaxYear || this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR || '2024'
    };

    this.jsonObjectFactory.getJSONObj(
      this.GENERAL_CONFIG.base_url + '/loadJsonObject?action_code=ejsaqd',
      loadParams,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
        } else if (data.callSuccess === '1' && data.jsonObject && Array.isArray(data.jsonObject) && data.jsonObject.length > 0) {
          this.selectedSicCodesTemp = [];
          data.jsonObject.forEach((item: any) => {
            if (item.CODE_GROUP == "SIC_CODE") {
              this.selectedSicCodesTemp.push(item);
            }
          });
          this.mapSelectedSicCodes();
        } else {
          this.showOptionSicCodeSelect = true;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading saved data:', error);
        this.showOptionSicCodeSelect = true;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Map selected SIC codes from saved data to available SIC codes
   */
  mapSelectedSicCodes(): void {
    this.selectedSicCodes = [];
    if (this.sicCodeData && this.sicCodeData.length > 0 &&
        this.selectedSicCodesTemp && this.selectedSicCodesTemp.length > 0) {
      this.selectedSicCodesTemp.forEach((selected: any) => {
        const matched = this.sicCodeData.find((sicCollection: any) =>
          (selected.CODE_KEY === sicCollection.CODE_KEY) && (String(selected.ME_KEY) === this.selectedMEKey)
        );
        if (matched) {
          this.selectedSicCodes.push(matched);
        }
      });
    }
    // Initialize filtered data for searchable dropdown
    this.sicCodeFilteredData = [...this.sicCodeData];
    this.showOptionSicCodeSelect = true;
    this.cdr.detectChanges();
  }

  // Searchable multiselect methods
  filterSicCodeDropdown(): void {
    if (!this.sicCodeData || this.sicCodeData.length === 0) {
      this.sicCodeFilteredData = [];
      return;
    }
    
    // Apply search filter if search text exists
    let filtered = [...this.sicCodeData];
    if (this.sicCodeSearchText && this.sicCodeSearchText.trim() !== '') {
      const searchLower = this.sicCodeSearchText.toLowerCase().trim();
      filtered = filtered.filter((item: any) => {
        const displayValue = item.SIC_CODE_DESC || item.CODE_DISP_DESC || '';
        return displayValue.toLowerCase().includes(searchLower);
      });
    }
    
    this.sicCodeFilteredData = filtered;
  }
  
  onSicCodeSearchChange(): void {
    this.filterSicCodeDropdown();
  }
  
  toggleSicCodeDropdown(): void {
    this.sicCodeDropdownOpen = !this.sicCodeDropdownOpen;
    if (this.sicCodeDropdownOpen) {
      this.sicCodeSearchText = '';
      this.filterSicCodeDropdown();
    }
  }
  
  closeSicCodeDropdown(): void {
    if (this.sicCodeDropdownOpen) {
      this.sicCodeDropdownOpen = false;
      this.sicCodeSearchText = '';
      this.cdr.detectChanges();
    }
  }

  /**
   * Check if the click event target is a checkbox input
   * Used to prevent double-toggle when clicking checkbox
   */
  isCheckboxClick(event: any): boolean {
    const target = event.target as HTMLElement;
    return target && target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox';
  }

  isSicCodeSelected(sicCodeItem: any): boolean {
    return this.selectedSicCodes.some(item => item.CODE_KEY === sicCodeItem.CODE_KEY);
  }

  toggleSicCodeSelection(sicCodeItem: any): void {
    const index = this.selectedSicCodes.findIndex(item => item.CODE_KEY === sicCodeItem.CODE_KEY);
    if (index > -1) {
      this.selectedSicCodes.splice(index, 1);
    } else {
      this.selectedSicCodes.push(JSON.parse(JSON.stringify(sicCodeItem)));
    }
    this.cdr.detectChanges();
  }

  selectAllSicCodes(): void {
    // Select all items in filtered data
    this.sicCodeFilteredData.forEach((item: any) => {
      const isSelected = this.selectedSicCodes.some(selected => selected.CODE_KEY === item.CODE_KEY);
      if (!isSelected) {
        this.selectedSicCodes.push(JSON.parse(JSON.stringify(item)));
      }
    });
    this.cdr.detectChanges();
  }

  unselectAllSicCodes(): void {
    // Unselect all items in filtered data
    this.sicCodeFilteredData.forEach((item: any) => {
      const index = this.selectedSicCodes.findIndex(selected => selected.CODE_KEY === item.CODE_KEY);
      if (index > -1) {
        this.selectedSicCodes.splice(index, 1);
      }
    });
    this.cdr.detectChanges();
  }

  getSelectedSicCodesDisplay(): string {
    if (!this.selectedSicCodes || this.selectedSicCodes.length === 0) {
      return '';
    }
    
    if (this.selectedSicCodes.length === 1) {
      return this.selectedSicCodes[0].SIC_CODE_DESC || this.selectedSicCodes[0].CODE_DISP_DESC || '';
    }
    
    return `${this.selectedSicCodes.length} selected`;
  }

  reset(): void {
    if (this.operationType == 'I') {
      // Add mode: Clear all fields
      this.selectedMEKey = '';
      this.selectedSicCodes = [];
      this.sicCodeDataTemp = _.clone(this.sicCodeData);
      this.sicCodeData = [];
      // Reset searchable dropdown state
      this.sicCodeDropdownOpen = false;
      this.sicCodeSearchText = '';
      this.sicCodeFilteredData = [];
      setTimeout(() => {
        this.sicCodeData = _.clone(this.sicCodeDataTemp);
        this.sicCodeFilteredData = [...this.sicCodeData];
        this.cdr.detectChanges();
      }, 100);
      this.loadInitialData();
    } else {
      // Edit mode: Restore original values
      this.loadInitialData();
    }
  }

  save(): void {
    // Prevent double-click
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar(
        'Please wait while we save this request',
        'gtw-snackbar--yellow'
      );
      return;
    }

    // Check lock status before saving
    if (this.isLocked) {
      this.gtwSnackbarService.openSnackBar(
        'You cannot perform this action as Tax return is already filed.',
        'gtw-snackbar--red'
      );
      return;
    }

    // Validation
    if (!this.selectedSicCodes || this.selectedSicCodes.length === 0 ||
        !this.selectedMEKey || this.selectedMEKey === '' ||
        !this.selectedTaxYear || this.selectedTaxYear === '') {
      this.gtwSnackbarService.openSnackBar(
        'Please select/enter all the required fields.',
        'gtw-snackbar--red'
      );
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Build selected SIC code keys (comma-separated)
    let selectedSicCodeKey = '';
    for (let indx in this.selectedSicCodes) {
      selectedSicCodeKey = (selectedSicCodeKey === '') 
        ? this.selectedSicCodes[indx].CODE_KEY 
        : selectedSicCodeKey + ',' + this.selectedSicCodes[indx].CODE_KEY;
    }

    // Build save data
    const sicCodeJsonObject: any = {
      option_key: this.selectedSicCodes[0]?.OPTION_KEY || null,
      me_key: this.selectedMEKey || null,
      tax_year: this.selectedTaxYear || null,
      code_key: selectedSicCodeKey || null
    };

    const savedData: any[] = [];
    savedData.push(sicCodeJsonObject);

    const saveData = {
      data: savedData,
      tax_year: this.selectedTaxYear
    };

    // Call service to save
    this.meSicAssignmentService.saveParentMe(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(
            'Data saved/updated Successfully.',
            'gtw-snackbar--green'
          );
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while saving. Please try again.',
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
